// components/gif-picker/klipyApi.js
// Small provider layer so Chat/PostCreate do not depend on KLIPY's raw response shape.

const V2_BASE_URL = "https://api.klipy.com/v2";
const V1_BASE_URL = "https://api.klipy.com/api/v1";

const getApiStyle = () =>
  (process.env.REACT_APP_KLIPY_API_STYLE || "v2").toLowerCase();

const getDefaultApiKey = () => process.env.REACT_APP_KLIPY_API_KEY || "";

const pickV2Media = (mediaFormats = {}, preferred = []) => {
  for (const key of preferred) {
    const media = mediaFormats?.[key];
    if (media?.url) return media;
  }
  return null;
};

const normalizeV2Item = (item) => {
  const mediaFormats = item?.media_formats || {};

  const display =
    pickV2Media(mediaFormats, [
      "mediumgif",
      "gif",
      "tinygif",
      "nanogif",
      "preview",
    ]) || {};

  const preview =
    pickV2Media(mediaFormats, [
      "tinygif",
      "nanogif",
      "preview",
      "mediumgif",
      "gif",
    ]) || display;

  const dims = Array.isArray(display?.dims) ? display.dims : [];

  return {
    id: String(item?.id ?? ""),
    slug: item?.slug || "",
    title: item?.content_description || item?.title || "GIF",
    url: display?.url || "",
    previewUrl: preview?.url || display?.url || "",
    width: Number(display?.width || dims?.[0]) || null,
    height: Number(display?.height || dims?.[1]) || null,
    tags: Array.isArray(item?.tags) ? item.tags : [],
    sourceUrl: item?.itemurl || item?.url || null,

    // Keep these if KLIPY supplies them for a particular item.
    creator:
      item?.creator?.username ||
      item?.user?.username ||
      item?.username ||
      null,
    contentSource:
      item?.content_source ||
      item?.source ||
      item?.source_tld ||
      null,

    provider: "klipy",
  };
};

const pickV1File = (item, sizeOrder = ["md", "hd", "sm", "xs"]) => {
  for (const size of sizeOrder) {
    const gif = item?.file?.[size]?.gif;
    if (gif?.url) return gif;
  }
  return null;
};

const normalizeV1Item = (item) => {
  const display =
    pickV1File(item, ["md", "hd", "sm", "xs"]) || {};
  const preview =
    pickV1File(item, ["sm", "xs", "md", "hd"]) || display;

  return {
    id: String(item?.id ?? ""),
    slug: item?.slug || "",
    title: item?.title || "GIF",
    url: display?.url || "",
    previewUrl: preview?.url || display?.url || "",
    width: Number(display?.width) || null,
    height: Number(display?.height) || null,
    tags: Array.isArray(item?.tags) ? item.tags : [],
    sourceUrl: item?.slug
      ? `https://klipy.com/gif/${item.slug}`
      : null,
    creator:
      item?.creator?.username ||
      item?.user?.username ||
      item?.username ||
      null,
    contentSource:
      item?.content_source || item?.source || null,
    provider: "klipy",
  };
};

const fetchJson = async (url, signal) => {
  const response = await fetch(url, { signal });

  if (!response.ok) {
    let details = "";
    try {
      details = await response.text();
    } catch (error) {}

    throw new Error(
      `KLIPY request failed (${response.status})${
        details ? `: ${details.slice(0, 180)}` : ""
      }`,
    );
  }

  return response.json();
};

const fetchV2 = async ({
  apiKey,
  query,
  limit = 24,
  pos = "",
  signal,
}) => {
  const params = new URLSearchParams({
    key: apiKey,
    limit: String(limit),
    media_filter: "gif,tinygif,mediumgif,nanogif,preview",
  });

  if (query) params.set("q", query);
  if (pos) params.set("pos", pos);

  const endpoint = query ? "search" : "featured";
  const json = await fetchJson(
    `${V2_BASE_URL}/${endpoint}?${params.toString()}`,
    signal,
  );

  return {
    items: (Array.isArray(json?.results) ? json.results : [])
      .map(normalizeV2Item)
      .filter((item) => item.url),
    next: json?.next || "",
  };
};

const fetchV1 = async ({
  apiKey,
  query,
  limit = 24,
  page = 1,
  signal,
}) => {
  const endpoint = query ? "search" : "trending";
  const params = new URLSearchParams({
    per_page: String(limit),
    page: String(page),
  });

  if (query) params.set("q", query);

  const json = await fetchJson(
    `${V1_BASE_URL}/${encodeURIComponent(apiKey)}/gifs/${endpoint}?${params.toString()}`,
    signal,
  );

  const pageData = json?.data || json || {};
  const rawItems = Array.isArray(pageData?.data)
    ? pageData.data
    : Array.isArray(pageData?.results)
      ? pageData.results
      : [];

  return {
    items: rawItems.map(normalizeV1Item).filter((item) => item.url),
    next: pageData?.has_next ? String((pageData?.current_page || page) + 1) : "",
  };
};

export const fetchKlipyGifs = async ({
  apiKey = getDefaultApiKey(),
  query = "",
  limit = 24,
  cursor = "",
  signal,
}) => {
  if (!apiKey) {
    throw new Error(
      "Missing KLIPY API key. Add REACT_APP_KLIPY_API_KEY to your .env file.",
    );
  }

  const cleanQuery = query.trim();
  const apiStyle = getApiStyle();

  if (apiStyle === "v1") {
    return fetchV1({
      apiKey,
      query: cleanQuery,
      limit,
      page: Number(cursor) || 1,
      signal,
    });
  }

  return fetchV2({
    apiKey,
    query: cleanQuery,
    limit,
    pos: cursor,
    signal,
  });
};
